import { ReactNode, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Text } from '@chakra-ui/react'

import {
  AiOperatorIcon,
  AlarmIcon,
  CameraIcon,
  ClockIcon,
  ColorScheme,
  DoorIcon,
  FacilityPill,
  FilmIcon,
  GuardsIcon,
  MailIcon,
  ManualIcon,
  OperatorAvatar,
  QuickReportIcon,
  StatusPill,
  StatusPillVariant,
  Tooltip,
  TooltipPlacement,
  UserAvatar,
  tokenVar,
} from '@hivewatch/dark'

import {
  DeviceType,
  IncidentSource,
  IncidentStatus,
} from '@/graphql/generated/schemas'
import { getArchivedLabel, getArchivedLabelWithSN } from '@/utils/formats'
import {
  getIncidentSourceDisplayName,
  getIncidentStatusDisplayName,
  isAIOperator,
} from '@/utils/incidents'

import {
  FacilityRowDataT,
  IncidentOrigin,
  IncidentStatusOwner,
  IncidentsListType,
} from '../../types/types'
import { getIsReleased, getTimeSinceDispatched } from '../../utils/helpers'

const ICON_SIZE = 18

// Secondary icon colour resolved from the design-library token (with a hex
// fallback) rather than a hardcoded CSS var.
const iconColor = (colorScheme: ColorScheme): string =>
  tokenVar(
    colorScheme === 'light'
      ? 'colors.light.icon.secondary'
      : 'colors.icon.secondary'
  )

/**
 * Wraps a cell in the dark `Tooltip`, which is anchor-based: we track the hovered
 * element and hand it to the portalled tooltip. Renders no tooltip when `label`
 * is empty. Replaces the legacy Chakra `Tooltip` on the reskinned cells.
 */
export const TooltipTarget = ({
  label,
  colorScheme,
  placement = 'top',
  dataTestId,
  children,
}: {
  label?: string
  colorScheme: ColorScheme
  placement?: TooltipPlacement
  dataTestId?: string
  children: ReactNode
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  return (
    <Box
      alignItems='center'
      as='span'
      data-testid={dataTestId}
      display='inline-flex'
      maxWidth='100%'
      minWidth={0}
      onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
      onMouseLeave={() => setAnchorEl(null)}
    >
      {children}
      {label && (
        <Tooltip
          anchorEl={anchorEl}
          colorScheme={colorScheme}
          label={label}
          placement={placement}
        />
      )}
    </Box>
  )
}

const statusVariant = (status?: IncidentStatus): StatusPillVariant => {
  switch (status) {
    case IncidentStatus.Resolved:
      return 'complete'
    case IncidentStatus.InProgress:
      return 'in-progress'
    case IncidentStatus.Active:
    default:
      return 'incomplete'
  }
}

type StatusTooltipContext = 'status' | 'clip' | 'ai'

export const StatusCell = ({
  incidentStatusOwner,
  colorScheme,
}: {
  incidentStatusOwner: IncidentStatusOwner
  colorScheme: ColorScheme
}) => {
  const { t } = useTranslation()
  const { status, isFlagged, hasVideoClip, owner } = incidentStatusOwner
  // Matches legacy precedence: the AI-operator icon wins; the clip icon only
  // shows when there's a video clip and the AI icon isn't being shown.
  const shouldShowAIOperatorIcon = isAIOperator(owner) || isFlagged
  const shouldShowClipIcon = Boolean(hasVideoClip) && !shouldShowAIOperatorIcon
  const statusLabel = getIncidentStatusDisplayName(status, isFlagged)

  const [tooltipContext, setTooltipContext] =
    useState<StatusTooltipContext>('status')

  const tooltipLabel =
    tooltipContext === 'clip'
      ? t('incidents:col_video_clip')
      : tooltipContext === 'ai'
      ? t('incidents:col_ai_reviewed')
      : statusLabel

  let icon: ReactNode
  if (shouldShowAIOperatorIcon) {
    icon = (
      <Box
        as='span'
        data-testid='incidentsPage_table_statusAIOperatorIcon'
        display='inline-flex'
        onMouseEnter={() => setTooltipContext('ai')}
        onMouseLeave={() => setTooltipContext('status')}
        sx={{ '& svg': { width: '14px', height: '14px' } }}
      >
        <AiOperatorIcon strokeColor='currentColor' />
      </Box>
    )
  } else if (shouldShowClipIcon) {
    icon = (
      <Box
        as='span'
        data-testid='incidentsPage_table_statusVideoClipIcon'
        display='inline-flex'
        onMouseEnter={() => setTooltipContext('clip')}
        onMouseLeave={() => setTooltipContext('status')}
      >
        <FilmIcon color='currentColor' size={14} />
      </Box>
    )
  }

  return (
    <TooltipTarget colorScheme={colorScheme} label={tooltipLabel}>
      <StatusPill
        colorScheme={colorScheme}
        data-testid='incidentsPage_table_statusCell'
        icon={icon}
        label={statusLabel}
        variant={statusVariant(status)}
      />
    </TooltipTarget>
  )
}

const SourceIcon = ({
  source,
  colorScheme,
}: {
  source?: IncidentOrigin
  colorScheme: ColorScheme
}) => {
  const color = iconColor(colorScheme)
  switch (source?.type) {
    case DeviceType.Alarm:
      return <AlarmIcon color={color} size={ICON_SIZE} />
    case DeviceType.Door:
      return <DoorIcon color={color} size={ICON_SIZE} />
    case DeviceType.Camera:
      return <CameraIcon color={color} size={ICON_SIZE} />
    case IncidentSource.Guard:
      return <GuardsIcon color={color} size={ICON_SIZE} />
    case IncidentSource.QuickReport:
    case IncidentSource.CustomQuickReport:
      return <QuickReportIcon color={color} size={ICON_SIZE} />
    case IncidentSource.Sensoscientific:
    case IncidentSource.HoneywellMassComms:
    case IncidentSource.Email:
      return <MailIcon color={color} size={ICON_SIZE} />
    default:
      return <ManualIcon color={color} size={ICON_SIZE} />
  }
}

export const SourceCell = ({
  source,
  colorScheme,
}: {
  source?: IncidentOrigin
  colorScheme: ColorScheme
}) => (
  <TooltipTarget
    colorScheme={colorScheme}
    label={source ? getIncidentSourceDisplayName(source) : undefined}
  >
    <Box alignItems='center' display='flex' gap='8px' minWidth={0}>
      <Box flexShrink={0}>
        <SourceIcon colorScheme={colorScheme} source={source} />
      </Box>
      <Text
        color={`text.primary.${colorScheme}`}
        data-testid='source-name-cell'
        isTruncated
      >
        {source?.name}
      </Text>
    </Box>
  </TooltipTarget>
)

export const FacilityCell = ({
  facility,
  colorScheme,
}: {
  facility?: FacilityRowDataT
  colorScheme: ColorScheme
}) =>
  facility?.name ? (
    <TooltipTarget
      colorScheme={colorScheme}
      label={getArchivedLabelWithSN(facility)}
    >
      <FacilityPill
        colorScheme={colorScheme}
        data-testid='incidentsPage_table_facilityNameCell'
      >
        {getArchivedLabel(facility.name, facility.archived)}
      </FacilityPill>
    </TooltipTarget>
  ) : null

export const OperatorCell = ({
  operator,
  colorScheme,
}: {
  operator?: string
  colorScheme: ColorScheme
}) => {
  if (!operator) {
    return null
  }

  // OperatorAvatar owns the AI-vs-human branch (dedicated glyph vs initials),
  // shared with the incident drawer's timeline feed. ENG-32096
  return <OperatorAvatar colorScheme={colorScheme} name={operator} />
}

/**
 * Guard cell — preserves the legacy behaviour: nothing when not dispatched, a
 * clock (with the dispatch-time tooltip) while awaiting a guard, otherwise the
 * guard's avatar. No avatar is shown when there is no guard.
 */
export const GuardCell = ({
  incident,
  colorScheme,
}: {
  incident?: IncidentsListType
  colorScheme: ColorScheme
}) => {
  const hasGuard = Boolean(incident?.guard)
  const hasDispatched = incident?.dispatched
  const guard = hasGuard
    ? `${incident?.guard?.firstName} ${incident?.guard?.lastName}`
    : null

  if (!hasDispatched) {
    return null
  }

  if (hasDispatched && !hasGuard && !getIsReleased(incident, hasGuard)) {
    return (
      <TooltipTarget
        colorScheme={colorScheme}
        label={getTimeSinceDispatched(incident)}
      >
        <Box as='span' data-testid='clock-icon' display='inline-flex'>
          <ClockIcon color={iconColor(colorScheme)} size={20} />
        </Box>
      </TooltipTarget>
    )
  }

  return guard ? <UserAvatar colorScheme={colorScheme} name={guard} /> : null
}
